import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { get_app_state } from "../../../../state/store"
import { PowerType } from "../../../model/v3/interface"
import pub_sub from "../../../state/pub_sub"
import { clamp } from "../../../utils/clamp"
import { POWER_TYPE_COLORS } from "../../constants"
import { build_area_path } from "./build_area_path"


const VIEW_W = 300
const VIEW_H = 120
const PAD = { top: 4, right: 2, bottom: 4, left: 2 }
const PLOT_W = VIEW_W - PAD.left - PAD.right
const PLOT_H = VIEW_H - PAD.top - PAD.bottom
const GRIDLINES = { Y_GW: 10, X_HOURS: 24 }

const ORDERED_POWER_TYPES: PowerType[] = [
    "battery",
    "hydro_pumped_storage",
    "hydro_river",
    "solar",
    "wind",
    "gas",
    "nuclear",
]
// Reverse the order as the graphs are built from the bottom upwards
ORDERED_POWER_TYPES.reverse()


// const no_built_power_plants: AggregatedBuiltPowerPlant = {
//     battery: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//         store_capacity_MWh: 0,
//     },
//     hydro_pumped_storage: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//         store_capacity_MWh: 0,
//     },
//     wind: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//     },
//     solar: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//     },
//     gas: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//     },
//     nuclear: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//     },
//     hydro_river: {
//         count: 0,
//         capacity_MW: 0,
//         area_km2: 0,
//     },
// }

// const config: SimulationConfig = {
//     start_datetime: new Date("2005-01-01T0:00:00.000Z"),
//     end_datetime: new Date("2005-01-01T23:00:00.000Z"),
// }
// const capacity_factor_data = get_capacity_factor_data_stub({ wind_csv_text: `
//     time,8419587ffffffff
//     2005-01-01 00:00,50
//     2005-01-01 01:00,50
//     2005-01-01 02:00,50
//     2005-01-01 03:00,50
//     2005-01-01 04:00,50
//     2005-01-01 05:00,50
//     2005-01-01 06:00,50
//     2005-01-01 07:00,50
//     2005-01-01 08:00,100
//     2005-01-01 09:00,100
//     2005-01-01 10:00,100
//     2005-01-01 11:00,100
//     2005-01-01 12:00,100
//     2005-01-01 13:00,100
//     2005-01-01 14:00,100
//     2005-01-01 15:00,100
//     2005-01-01 16:00,100
//     2005-01-01 17:00,100
//     2005-01-01 18:00,100
//     2005-01-01 19:00,100
//     2005-01-01 20:00,100
//     2005-01-01 21:00,100
//     2005-01-01 22:00,100
//     2005-01-01 23:00,100`})
// const data_dependencies = get_test_model_data_inputs_stub(config, capacity_factor_data)

// data_dependencies.demand_GW_by_hour.data = [
//     10, 10, 10, 10,
//     20, 20, 20, 20,
//     20, 20, 20, 20,
//     10, 10, 10, 10,
//     10, 10, 10, 10,
//     10, 10, 10, 10,
// ]
// data_dependencies.initial_proportional_electricity_demand_GW_by_h3r4 = {
//     "8419587ffffffff": 1,
// }
// data_dependencies.initial_aggregated_power_plants_by_h3r4_id = {
//     "8419587ffffffff": {
//         ...no_built_power_plants,
//         battery: {
//             count: 1,
//             capacity_MW: 20_000,
//             area_km2: 0,
//             store_capacity_MWh: 20_000,
//         },
//         wind: {
//             count: 1,
//             capacity_MW: 20_000,
//             area_km2: 0,
//         }
//     }
// }

// const test_simulation_state = setup_model(config, data_dependencies)


export function EnergySupplyDemandGraph(props: { show_supply_by_type: boolean })
{
    const { show_supply_by_type } = props

    // let timesteps = test_simulation_state.timesteps
    let timesteps = get_app_state(state => state.simulation_state.sim_state?.timesteps) || []
    // const i_start = timesteps.length * (1 / 14)
    // const n = timesteps.length * (2 / 14)
    // timesteps = timesteps.slice(i_start, i_start + n)

    // Keep a ref so the pub_sub callback always sees the latest timesteps
    // without needing to re-subscribe on every render.
    const timesteps_ref = useRef(timesteps)
    timesteps_ref.current = timesteps

    const [current_x, set_current_x] = useState<number | null>(null)

    // Update the current-time indicator on every simulation tick
    useEffect(() => pub_sub.sub("simulation_datetime", payload =>
    {
        const steps = timesteps_ref.current
        const n = steps.length
        if (n < 2) return
        const { one, mix } = payload.datetime_sim_range_hourly_indices_mix
        const frac = Math.min(1, Math.max(0, (one + mix) / (n - 1)))
        set_current_x(PAD.left + frac * PLOT_W)
    }), [])


    const earliest_datetime_ms = timesteps[0]?.timestamp_ms ?? 0
    const latest_datetime_ms = timesteps.last()?.timestamp_ms ?? 0
    console.log("EnergySupplyDemandGraph: earliest_datetime_ms", new Date(earliest_datetime_ms), "latest_datetime_ms", new Date(latest_datetime_ms), "timesteps.length", timesteps.length)
    const datetime_range_ms = latest_datetime_ms - earliest_datetime_ms
    const force_set_simulation_datetime = useCallback((new_datetime_ms: number) =>
    {
        new_datetime_ms = clamp(new_datetime_ms, earliest_datetime_ms, latest_datetime_ms)

        // We might need to debounce this if performance is any issue.
        pub_sub.pub("simulation_force_set_current_datetime", { new_datetime_ms })
    }, [earliest_datetime_ms, latest_datetime_ms])

    // Pointer interaction state for dragging to set datetime
    const svg_ref = useRef<SVGSVGElement | null>(null)
    const [is_pointer_active, set_is_pointer_active] = useState(false)

    const pointer_set_datetime_from_event = useCallback((e: PointerEvent | React.PointerEvent) =>
    {
        if (!svg_ref.current) return
        const rect = svg_ref.current.getBoundingClientRect()
        debugger
        const x = (e as any).clientX
        const rel = (x - rect.left - PAD.left) / PLOT_W
        const frac = Math.min(1, Math.max(0, rel))
        const new_datetime_ms = Math.round(earliest_datetime_ms + frac * datetime_range_ms)
        force_set_simulation_datetime(new_datetime_ms)
    }, [earliest_datetime_ms, datetime_range_ms, force_set_simulation_datetime])


    // Recompute path data only when timesteps change (i.e. when a power plant is built)
    const graph_data = useMemo(() =>
    {
        const n = timesteps.length
        if (n < 2) return null

        let max_GW = 0
        for (const step of timesteps)
        {
            const total = step.generated_GW.primary + Math.max(0, step.generated_GW.storage)
            max_GW = Math.max(max_GW, step.demand_GW, step.generated_GW.primary, total)
        }
        if (max_GW === 0) max_GW = 1

        const to_x = (i: number) => Math.round(PAD.left + (i / (n - 1)) * PLOT_W)
        const to_y = (v: number) => Math.round(PAD.top + PLOT_H * (1 - v / max_GW))

        const xs: number[] = []
        const y_demand: number[] = []
        const y_primary: number[] = []
        const y_stored_primary: number[] = []
        // const y_max_demand_or_total_positive: number[] = []
        const y_excess: number[] = []
        const y_total_positive: number[] = []

        const y_by_power_base: number[] = []
        const y_by_power_type: number[][] = ORDERED_POWER_TYPES.map(() => [])

        for (let i = 0; i < n; i++)
        {
            const step = timesteps[i]!
            xs.push(to_x(i))
            y_demand.push(to_y(step.demand_GW))

            y_primary.push(to_y(step.generated_GW.primary))

            const stored_primary = Math.min(step.generated_GW.primary, step.demand_GW) + Math.max(0, -step.generated_GW.storage)
            y_stored_primary.push(to_y(stored_primary))
            // const max_demand_or_total_positive = Math.max(step.demand_GW, step.generated_GW.total_positive)
            // y_max_demand_or_total_positive.push(to_y(max_demand_or_total_positive))
            y_total_positive.push(to_y(step.generated_GW.total_positive))
            y_excess.push(to_y(step.generated_GW.primary + step.generated_GW.storage))

            let power_y_sum = 0
            y_by_power_base.push(power_y_sum)

            ORDERED_POWER_TYPES.forEach((power_type, j) =>
            {
                const GW = step.generated_GW_by_type[power_type]
                const y = to_y(Math.max(GW, 0) + power_y_sum)
                y_by_power_type[j]!.push(y)
                power_y_sum += y
            })
        }

        const demand_line = xs
            .map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y_demand[i]!.toFixed(1)}`)
            .join(" ")

        const paths_by_power_type: { path: string, colour: string }[] = ORDERED_POWER_TYPES.map((power_type, i) =>
        {
            const y_fwd = y_by_power_type[i]!
            const y_bwd = y_by_power_type[i - 1] ?? y_by_power_base
            const path = build_area_path(xs, y_fwd, y_bwd, () => true)

            return { path, colour: POWER_TYPE_COLORS[power_type] }
        })

        // Red: power shortage — total generated is below demand
        // SVG: y_total > y_demand (further down the screen)
        const shortage_path = build_area_path(xs, y_demand, y_total_positive, (i: number) =>
        {
            const step = timesteps[i]!
            return step.generated_GW.total_positive < step.demand_GW
        })

        // Dark blue: storage is absorbing excess (storage <= 0 and primary exceeds demand)
        // Shown as the band between the primary line (above) and demand line (below)
        const energy_to_storage_path = build_area_path(xs, y_stored_primary, y_demand, i =>
        {
            const step = timesteps[i]!
            return step.generated_GW.storage < 0 && step.generated_GW.primary > step.demand_GW
        })

        // Light pink: energy that can not be absored by storage
        const excess_energy_path = build_area_path(xs, y_excess, y_demand, i =>
        {
            const step = timesteps[i]!
            return step.generated_GW.primary > step.demand_GW
            // return step.excess_GW > 0
        })

        // Light blue: storage is releasing power (storage > 0)
        // Shown as the band between the total line (above) and primary line (below)
        const energy_from_storage_path = build_area_path(xs, y_total_positive, y_primary, i =>
        {
            const step = timesteps[i]!
            return step.generated_GW.storage > 0
        })

        // Y gridlines: every GRIDLINES.Y_GW GW
        const y_gridlines: number[] = []
        for (let gw = GRIDLINES.Y_GW; gw < max_GW; gw += GRIDLINES.Y_GW)
        {
            y_gridlines.push(to_y(gw))
        }

        // X gridlines: every GRIDLINES.X_HOURS hours (1 timestep = 1 hour)
        const x_gridlines: number[] = []
        for (let i = GRIDLINES.X_HOURS; i < n; i += GRIDLINES.X_HOURS)
        {
            x_gridlines.push(to_x(i))
        }

        return { demand_line, paths_by_power_type, shortage_path, energy_to_storage_path, excess_energy_path, energy_from_storage_path, y_gridlines, x_gridlines }
    }, [timesteps])

    if (!graph_data) return null

    return (
        <svg
            ref={svg_ref}
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            style={{ width: VIEW_W, height: VIEW_H, display: "block", touchAction: "none" }}
            preserveAspectRatio="none"
            onPointerDown={(e) =>
            {
                try { (e.target as Element).setPointerCapture?.(e.pointerId) } catch {}
                set_is_pointer_active(true)
                pointer_set_datetime_from_event(e)
            }}
            onPointerMove={(e) =>
            {
                if (!is_pointer_active) return
                pointer_set_datetime_from_event(e)
            }}
            onPointerUp={(e) =>
            {
                try { (e.target as Element).releasePointerCapture?.(e.pointerId) } catch {}
                set_is_pointer_active(false)
                pointer_set_datetime_from_event(e)
            }}
            onPointerLeave={() => set_is_pointer_active(false)}
        >
            {/* background so graph has white backdrop when rendered over other UI */}
            <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill="#ffffff" />
            {/* highlight when pointer interaction is active */}
            {is_pointer_active && (
                <rect x={0.5} y={0.5} width={VIEW_W - 1} height={VIEW_H - 1} fill="none" stroke="rgba(30,130,220,0.9)" strokeWidth={1} rx={3} ry={3} />
            )}
            {/* Grid lines */}
            {graph_data.y_gridlines.map(y => (
                <line key={`gy${y}`}
                    x1={PAD.left} y1={y}
                    x2={VIEW_W - PAD.right} y2={y}
                    stroke="rgba(0,0,0,0.1)"
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                />
            ))}
            {graph_data.x_gridlines.map(x => (
                <line key={`gx${x}`}
                    x1={x} y1={PAD.top}
                    x2={x} y2={VIEW_H - PAD.bottom}
                    stroke="rgba(0,0,0,0.1)"
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                />
            ))}

            {/* Dark blue: excess power being absorbed by storage */}
            {!show_supply_by_type && graph_data.energy_to_storage_path && (
                <path d={graph_data.energy_to_storage_path} fill="rgba(20,80,180,0.55)" stroke="none" />
            )}
            {/* Light pink: excess power being wasted */}
            {graph_data.excess_energy_path && <>
                <defs>
                    <pattern id="diagonalStripes" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="4" height="4" fill="rgba(255, 132, 222, 0.55)" />
                        <rect x="0" y="0" width="2" height="4" fill="rgba(20,80,180,0.55)" />
                        {/* <rect x="0" y="0" width="4" height="8" fill="rgba(255, 132, 222, 0.85)" /> */}
                    </pattern>
                </defs>
                <path d={graph_data.excess_energy_path} fill="url(#diagonalStripes)" stroke="none" />
            </>}
            {/* Light blue: power being released from storage */}
            {!show_supply_by_type && graph_data.energy_from_storage_path && (
                <path d={graph_data.energy_from_storage_path} fill="rgba(100,180,255,0.55)" stroke="none" />
            )}

            {show_supply_by_type && graph_data.paths_by_power_type.map(({ path, colour }) =>
                <path d={path} fill={colour} stroke="none" />
            )}

            {/* Red: power shortage */}
            {graph_data.shortage_path && (
                <path d={graph_data.shortage_path} fill="rgba(220,50,50,0.65)" stroke="none" />
            )}

            {/* Demand line */}
            <path
                d={graph_data.demand_line}
                fill="none"
                stroke="rgba(0,0,0,0.75)"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
            />
            {/* Current time indicator */}
            {current_x !== null && (
                <line
                    x1={current_x.toFixed(1)} y1={PAD.top}
                    x2={current_x.toFixed(1)} y2={VIEW_H - PAD.bottom}
                    stroke="rgba(210,30,0,0.3)"
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                />
            )}
        </svg>
    )
}
